import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import apiClient from '../config/api';
import { buildIndexedOwnerEntriesSnapshot, extractPdfOwnerNoticeEntry } from '../pages/cao';
import AppearanceSelector from './document-viewer/AppearanceSelector';
import DarkModePdfCanvas from './document-viewer/DarkModePdfCanvas';
import DocumentMetadata from './document-viewer/DocumentMetadata';
import PdfSideNavigation from './document-viewer/PdfSideNavigation';
import { MAX_EXCEL_PREVIEW_BYTES } from '../utils/documentPreview';
import MsgReader from '@kenjiuno/msgreader';
import PostalMime from 'postal-mime';
import * as XLSX from 'xlsx';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/homeDocumentViewer.css';
import '../styles/cao.css';

const MIN_ZOOM = 30;
const MAX_ZOOM = 250;
const ZOOM_STEP = 10;
const LARGE_PDF_PAGE_THRESHOLD = 100;
const OWNER_INDEX_BATCH_SIZE = 20;
const OWNER_SEARCH_RESULT_LIMIT = 12;
const UP_ARROW = '\u25B2';
const DOWN_ARROW = '\u25BC';

const formatEmailAddress = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry.trim();
  const name = String(entry.name || '').trim();
  const address = String(entry.address || entry.email || '').trim();
  return name && address ? `${name} <${address}>` : (address || name);
};

const formatEmailAddresses = (entries) => (Array.isArray(entries)
  ? entries.map(formatEmailAddress).filter(Boolean).join(', ')
  : '');

const getAttachmentMimeType = (fileName, suppliedType = '') => {
  if (suppliedType) return suppliedType;
  const extension = String(fileName || '').split('.').pop().toLowerCase();
  return ({
    pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    eml: 'message/rfc822', msg: 'application/vnd.ms-outlook',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  })[extension] || 'application/octet-stream';
};

const toAttachmentBlob = (content, mimeType) => {
  if (content instanceof Blob) return content;
  if (content instanceof ArrayBuffer || ArrayBuffer.isView(content)) return new Blob([content], { type: mimeType });
  if (typeof content === 'string') {
    try {
      const decoded = atob(content.replace(/\s+/g, ''));
      return new Blob([Uint8Array.from(decoded, (character) => character.charCodeAt(0))], { type: mimeType });
    } catch (_error) {
      return new Blob([content], { type: mimeType });
    }
  }
  return new Blob([], { type: mimeType });
};

const splitEmailThread = (body) => {
  const lines = String(body || '').replace(/\r\n?/g, '\n').split('\n');
  const boundaries = [];

  lines.forEach((line, index) => {
    if (index === 0) return;
    const trimmed = line.trim();
    const isOriginalMessage = /^-{2,}\s*Original Message\s*-{2,}$/i.test(trimmed);
    const isQuotedReply = /^On\s.+\swrote:\s*$/i.test(trimmed);
    const isDividerBeforeHeaders = /^_{5,}$/.test(trimmed)
      && /^From:\s*\S+/im.test(lines.slice(index + 1, index + 4).join('\n'));
    const looksLikeHeaderBlock = /^From:\s*\S+/i.test(trimmed)
      && /^(Sent|Date|To|Subject):\s*\S+/im.test(lines.slice(index + 1, index + 7).join('\n'));

    if (isOriginalMessage || isQuotedReply || isDividerBeforeHeaders || looksLikeHeaderBlock) {
      const previousBoundary = boundaries[boundaries.length - 1];
      if (previousBoundary === undefined || index - previousBoundary > 1) boundaries.push(index);
    }
  });

  const starts = [0, ...boundaries];
  return starts
    .map((start, index) => lines.slice(start, starts[index + 1] ?? lines.length).join('\n').trim())
    .filter(Boolean);
};

const getExcelColumnLabel = (columnIndex) => {
  let label = '';
  let current = columnIndex + 1;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    current = Math.floor((current - 1) / 26);
  }
  return label;
};

const compareExcelCellValues = (leftValue, rightValue) => {
  const leftText = String(leftValue ?? '').trim();
  const rightText = String(rightValue ?? '').trim();
  const leftNumber = Number(leftText.replace(/,/g, ''));
  const rightNumber = Number(rightText.replace(/,/g, ''));
  if (leftText && rightText && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber - rightNumber;
  return leftText.localeCompare(rightText, undefined, { numeric: true, sensitivity: 'base' });
};

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const getDefaultZoom = () => (
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches ? 100 : 50
);

const HomeDocumentViewer = ({ file, categoryName, typeName, metadata, loadDocument, onClose }) => {
  const normalizedType = String(file.FileType || '').trim().toLowerCase();
  const extension = String(file.FileName || '').split('.').pop().toLowerCase();
  const isPdf = normalizedType === 'application/pdf' || extension === 'pdf';
  const isImage = normalizedType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'tif', 'tiff'].includes(extension);
  const isExcel = ['xls', 'xlsx'].includes(extension)
    || normalizedType.includes('spreadsheet')
    || normalizedType.includes('excel');
  const isEml = extension === 'eml' || normalizedType === 'message/rfc822';
  const isMsg = extension === 'msg' || normalizedType === 'application/vnd.ms-outlook';
  const previewTypeName = isPdf
      ? 'PDF'
      : isExcel
        ? 'Excel workbook'
        : isImage
          ? 'image'
          : (isEml || isMsg)
            ? 'email'
            : 'document';
  const loadingPreviewMessage = `Loading ${previewTypeName} preview…`;
  const initialZoom = getDefaultZoom();
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [displayedPage, setDisplayedPage] = useState(1);
  const [isPageRendering, setIsPageRendering] = useState(false);
  const [zoom, setZoom] = useState(initialZoom);
  const [zoomInput, setZoomInput] = useState(String(initialZoom));
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoadingFile, setIsLoadingFile] = useState(true);
  const [appearance, setAppearance] = useState('light');
  const [excelSheets, setExcelSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [excelFilters, setExcelFilters] = useState({});
  const [excelSortConfig, setExcelSortConfig] = useState({ column: null, direction: 'asc' });
  const [emailPreview, setEmailPreview] = useState(null);
  const [embeddedAttachment, setEmbeddedAttachment] = useState(null);
  const [viewerWidth, setViewerWidth] = useState(0);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [ownerIndex, setOwnerIndex] = useState({ status: 'idle', scannedPages: 0, entries: [], error: '' });
  const [ownerQuery, setOwnerQuery] = useState('');
  const viewerBodyRef = useRef(null);
  const renderStateRef = useRef({ pageNumber: 0, canvasReady: false });
  const isOwnerIndexEligible = Number(file.AttachmentTypeID) === 68 && pageCount > LARGE_PDF_PAGE_THRESHOLD;
  const ownerSearchResults = ownerQuery.trim()
    ? ownerIndex.entries
      .filter((entry) => entry.displayName.toLowerCase().includes(ownerQuery.trim().toLowerCase()))
      .slice(0, OWNER_SEARCH_RESULT_LIMIT)
    : [];
  const activeExcelRows = excelSheets.find((sheet) => sheet.name === activeSheet)?.rows || [];
  const excelColumnCount = activeExcelRows.reduce((maximum, row) => Math.max(maximum, row?.length || 0), 0);
  const excelHeaderRow = activeExcelRows[0] || [];
  const excelBodyRows = activeExcelRows.slice(1).map((row, index) => ({ row, sheetRowNumber: index + 2 }));
  const filteredExcelRows = excelBodyRows.filter(({ row }) => (
    Object.entries(excelFilters).every(([columnIndex, value]) => String(row[columnIndex] ?? '').toLowerCase().includes(String(value).trim().toLowerCase()))
  ));
  const sortedExcelRows = excelSortConfig.column === null
    ? filteredExcelRows
    : [...filteredExcelRows].sort((left, right) => compareExcelCellValues(left.row[excelSortConfig.column], right.row[excelSortConfig.column]) * (excelSortConfig.direction === 'desc' ? -1 : 1));
  const emailThreadMessages = splitEmailThread(emailPreview?.body);

  useEffect(() => {
    if (!isOwnerIndexEligible || !pdfDocument) {
      setOwnerIndex({ status: 'idle', scannedPages: 0, entries: [], error: '' });
      return undefined;
    }

    let cancelled = false;
    const scanOwners = async () => {
      setOwnerIndex({ status: 'checking', scannedPages: 0, entries: [], error: '' });
      try {
        const firstEntry = await extractPdfOwnerNoticeEntry(pdfDocument, 1);
        if (cancelled) return;
        if (!firstEntry) {
          setOwnerIndex({ status: 'unsupported', scannedPages: 1, entries: [], error: 'Owner layout was not found in this PDF.' });
          return;
        }

        const entries = [{ ...firstEntry, startPage: 1, endPage: pageCount }];
        setOwnerIndex({ status: 'indexing', scannedPages: 1, entries: buildIndexedOwnerEntriesSnapshot(entries, 1, pageCount), error: '' });

        for (let currentPage = 2; currentPage <= pageCount; currentPage += 1) {
          const entry = await extractPdfOwnerNoticeEntry(pdfDocument, currentPage);
          if (cancelled) return;
          if (entry) {
            entries[entries.length - 1].endPage = currentPage - 1;
            entries.push({ ...entry, startPage: currentPage, endPage: pageCount });
          }
          if (currentPage % OWNER_INDEX_BATCH_SIZE === 0 || currentPage === pageCount) {
            setOwnerIndex({
              status: currentPage === pageCount ? 'ready' : 'indexing',
              scannedPages: currentPage,
              entries: buildIndexedOwnerEntriesSnapshot(entries, currentPage, pageCount),
              error: ''
            });
            if (currentPage < pageCount) await new Promise((resolve) => window.setTimeout(resolve, 0));
          }
        }
      } catch (indexError) {
        console.error('Failed to index Documents PDF owners:', indexError);
        if (!cancelled) setOwnerIndex({ status: 'error', scannedPages: 0, entries: [], error: 'Unable to build owner index for this PDF.' });
      }
    };

    const timeoutId = window.setTimeout(scanOwners, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isOwnerIndexEligible, pageCount, pdfDocument]);

  useEffect(() => {
    if (!isPdf || !pageCount || !viewerWidth) return;
    renderStateRef.current = { pageNumber, canvasReady: false };
    setIsPageRendering(true);
  }, [isPdf, pageCount, pageNumber, rotation, viewerWidth, zoom]);

  const markRenderComplete = (stage, renderedPage) => {
    const renderState = renderStateRef.current;
    if (renderState.pageNumber !== renderedPage || stage !== 'canvas') return;
    renderState.canvasReady = true;
    if (!renderState.canvasReady) return;
    setDisplayedPage(renderedPage);
    setIsPageRendering(false);
  };

  useEffect(() => {
    if (!isPdf) return undefined;
    const updateWidth = () => {
      const containerWidth = viewerBodyRef.current?.clientWidth || 0;
      setViewerWidth(containerWidth ? Math.max(240, containerWidth - 32) : 0);
    };
    updateWidth();
    if (typeof ResizeObserver === 'function' && viewerBodyRef.current) {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(viewerBodyRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isPdf]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const loadPdf = async () => {
      setIsLoadingFile(true);
      setError('');
      try {
        const response = loadDocument
          ? await loadDocument({ file })
          : await apiClient.get(`/files/uploads/${file.FileID}/download`, {
            responseType: 'blob',
            withCredentials: true
          });
        if (cancelled) return;

        const responseData = response?.data ?? response;
        const pdfBlob = responseData instanceof Blob
          ? responseData
          : new Blob([responseData], { type: normalizedType || 'application/octet-stream' });
        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfUrl(objectUrl);

        if (isExcel) {
          const declaredSize = Number(file.FileSize || file.FileSizeBytes || file.Size || 0);
          const isTooLargeToPreview = Math.max(declaredSize, pdfBlob.size) >= MAX_EXCEL_PREVIEW_BYTES;
          if (isTooLargeToPreview) {
            setError('This Excel workbook is too large to preview safely. Use Download to open it in Excel.');
          } else {
            const workbook = XLSX.read(await pdfBlob.arrayBuffer(), { type: 'array' });
            const sheets = workbook.SheetNames.map((name) => ({
              name,
              rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: '' })
            }));
            setExcelSheets(sheets);
            setActiveSheet(sheets[0]?.name || '');
          }
        } else if (isEml) {
          const parsed = await new PostalMime().parse(await pdfBlob.arrayBuffer());
          setEmailPreview({
            subject: parsed.subject || '(No subject)',
            from: formatEmailAddress(parsed.from),
            to: formatEmailAddresses(parsed.to),
            cc: formatEmailAddresses(parsed.cc),
            bcc: formatEmailAddresses(parsed.bcc),
            date: parsed.date || '',
            body: parsed.text || String(parsed.html || '').replace(/<[^>]+>/g, ' '),
            attachments: (parsed.attachments || []).map((attachment, index) => {
              const fileName = attachment.filename || attachment.name || `attachment-${index + 1}`;
              const mimeType = getAttachmentMimeType(fileName, attachment.mimeType || attachment.contentType);
              const blob = toAttachmentBlob(attachment.content, mimeType);
              return { fileName, mimeType, blob };
            })
          });
        } else if (isMsg) {
          const reader = new MsgReader(await pdfBlob.arrayBuffer());
          const parsed = reader.getFileData();
          const recipients = Array.isArray(parsed.recipients) ? parsed.recipients : [];
          setEmailPreview({
            subject: parsed.subject || '(No subject)',
            from: parsed.senderEmail || parsed.senderName || '',
            to: formatEmailAddresses(recipients.filter((entry) => entry.recipType === 'to' || !entry.recipType)),
            cc: formatEmailAddresses(recipients.filter((entry) => entry.recipType === 'cc')),
            bcc: formatEmailAddresses(recipients.filter((entry) => entry.recipType === 'bcc')),
            date: parsed.messageDeliveryTime || '',
            body: parsed.body || '',
            attachments: (parsed.attachments || []).map((attachment, index) => {
              const attachmentData = reader.getAttachment(attachment);
              const fileName = attachmentData?.fileName || attachment.fileName || attachment.fileNameShort || `attachment-${index + 1}`;
              const mimeType = getAttachmentMimeType(fileName, attachment.mimeType);
              const blob = toAttachmentBlob(attachmentData?.content, mimeType);
              return { fileName, mimeType, blob };
            })
          });
        }
      } catch (loadError) {
        console.error(`Failed to fetch ${previewTypeName} preview:`, loadError);
        if (!cancelled) setError(`Unable to load ${previewTypeName} preview. Your session may have expired.`);
      } finally {
        if (!cancelled) setIsLoadingFile(false);
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, isExcel, isEml, isMsg, loadDocument, normalizedType, previewTypeName]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const updateZoom = (value) => {
    const parsed = Number(value);
    const next = Number.isFinite(parsed)
      ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(parsed)))
      : zoom;
    setZoom(next);
    setZoomInput(String(next));
  };

  const changePage = (nextPage) => {
    setPageNumber(Math.min(pageCount, Math.max(1, nextPage)));
  };

  const downloadFile = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = file.FileName || `document-${file.FileID}.pdf`;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openInNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const handleExcelFilterChange = (columnIndex, value) => {
    const next = { ...excelFilters };
    if (String(value).trim()) next[columnIndex] = value;
    else delete next[columnIndex];
    setExcelFilters(next);
  };

  const handleExcelSort = (columnIndex) => {
    const next = excelSortConfig.column === columnIndex
      ? { column: columnIndex, direction: excelSortConfig.direction === 'asc' ? 'desc' : 'asc' }
      : { column: columnIndex, direction: 'asc' };
    setExcelSortConfig(next);
  };

  const downloadEmbeddedAttachment = (attachment) => {
    const objectUrl = URL.createObjectURL(attachment.blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  if (embeddedAttachment) {
    return (
      <HomeDocumentViewer
        file={{ FileName: embeddedAttachment.fileName, FileType: embeddedAttachment.mimeType, FileSizeBytes: embeddedAttachment.blob.size }}
        metadata={metadata}
        loadDocument={() => embeddedAttachment.blob}
        onClose={() => setEmbeddedAttachment(null)}
      />
    );
  }

  return (
    <div className={`home-document-viewer home-document-viewer--${appearance} cao-document-viewer cao-document-viewer--${appearance}`} role="dialog" aria-modal="true" aria-label={`Preview ${file.FileName || 'document'}`}>
      <header className="cao-document-viewer__header">
        <button type="button" className="view-button cao-document-viewer__back" onClick={onClose}>
          <span aria-hidden="true">←</span><span>Back</span>
        </button>

        <div className="cao-document-viewer__meta">
          <span className="cao-document-viewer__name">{file.FileName || `Document ${file.FileID}`}</span>
          <span className="cao-document-viewer__type">{isPdf ? `PDF${pageCount ? ` | Page ${pageNumber} of ${pageCount}` : ''}` : normalizedType || 'Document'}</span>
        </div>

        <div className="cao-document-viewer__actions">
          <AppearanceSelector
            value={appearance}
            onChange={setAppearance}
            className="cao-document-viewer__appearance"
            includeButtonClassName="view-button"
            buttonClassName="cao-document-viewer__appearance-button"
            activeButtonClassName="cao-document-viewer__appearance-button--active"
          />
          {(isPdf || isImage) && <div className="cao-document-viewer__zoom" aria-label="Zoom controls">
            <button type="button" className="view-button cao-document-viewer__zoom-button" onClick={() => updateZoom(zoom - ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">−</button>
            <label className="cao-document-viewer__zoom-value">
              <input
                type="number"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                value={zoomInput}
                onChange={(event) => setZoomInput(event.target.value)}
                onBlur={() => updateZoom(zoomInput)}
                onFocus={(event) => event.target.select()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                  if (event.key === 'Escape') {
                    setZoomInput(String(zoom));
                    event.stopPropagation();
                    event.currentTarget.blur();
                  }
                }}
                aria-label={`Zoom percentage, ${MIN_ZOOM} to ${MAX_ZOOM}`}
              />
              <span aria-hidden="true">%</span>
            </label>
            <button type="button" className="view-button cao-document-viewer__zoom-button" onClick={() => updateZoom(zoom + ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">+</button>
          </div>}

          {isPdf && <button type="button" className="view-button cao-document-viewer__open-new-tab" onClick={openInNewTab} disabled={!pdfUrl}>Open PDF in new tab</button>}
          {(isPdf || isImage) && <button type="button" className="view-button cao-document-viewer__icon-action" onClick={() => setRotation((current) => (current + 90) % 360)} aria-label="Rotate" title="Rotate">↻</button>}
          <button type="button" className="view-button cao-document-viewer__download cao-document-viewer__icon-action" onClick={downloadFile} disabled={!pdfUrl} aria-label="Download" title="Download"><span aria-hidden="true">↓</span></button>
        </div>
      </header>

      <main
        ref={viewerBodyRef}
        className={
          'home-document-viewer__body cao-document-viewer__body'
          + (isPdf ? ' home-document-viewer__body--pdf' : '')
          + (isPdf ? ' cao-document-viewer__body--pdf cao-document-viewer__body--pdf-page-view' : '')
          + (isExcel ? ' home-document-viewer__body--excel' : '')
          + (isExcel ? ' cao-document-viewer__body--excel' : '')
          + (isEml || isMsg ? ' home-document-viewer__body--email' : '')
          + (isEml || isMsg ? ' cao-document-viewer__body--email' : '')
        }
      >
        <DocumentMetadata
          className="cao-document-viewer__context"
          itemClassName="cao-document-viewer__context-row"
          labelClassName="cao-document-viewer__context-label"
          valueClassName="cao-document-viewer__context-value"
          items={metadata || [
            { label: 'File ID', value: file.FileID },
            { label: 'Category', value: categoryName || file.AttachmentCategoryID || '—' },
            { label: 'Type', value: typeName || file.AttachmentTypeID || '—' }
          ]}
        />

        {isPdf && <div className="cao-document-viewer__pdf-tools"><div className="cao-document-viewer__pdf-nav">
          <div className="cao-document-viewer__pdf-nav-group cao-document-viewer__pdf-nav-group--page">
          <button type="button" className="view-button" onClick={() => changePage(pageNumber - 1)} disabled={pageNumber <= 1 || isPageRendering}>Prev</button>
          <span className="cao-document-viewer__pdf-label">Page</span>
          <input
            className="cao-document-viewer__pdf-page-input"
            type="number"
            min="1"
            max={pageCount || 1}
            value={pageNumber}
            onChange={(event) => changePage(Number(event.target.value) || 1)}
            disabled={isPageRendering}
            aria-label="PDF page"
          />
          <span className="cao-document-viewer__pdf-page-total">of {pageCount || '—'}</span>
          <button type="button" className="view-button" onClick={() => changePage(pageNumber + 1)} disabled={!pageCount || pageNumber >= pageCount || isPageRendering}>Next</button>
          </div>
        </div></div>}

        {isOwnerIndexEligible && ownerIndex.status !== 'idle' && (
          <section className="home-document-viewer__owner-search" aria-label="Search PDF by owner">
            <div className="home-document-viewer__owner-header">
              <b>Owner Search</b>
              <span>
                {ownerIndex.status === 'checking'
                  ? 'Checking whether owner search is available…'
                  : ownerIndex.status === 'indexing'
                    ? `Indexing owners: ${ownerIndex.scannedPages.toLocaleString()} / ${pageCount.toLocaleString()} pages`
                    : ownerIndex.status === 'ready'
                      ? `${ownerIndex.entries.length.toLocaleString()} owners indexed`
                      : ownerIndex.error}
              </span>
            </div>
            {ownerIndex.status !== 'unsupported' && ownerIndex.status !== 'error' && (
              <input
                type="search"
                value={ownerQuery}
                onChange={(event) => setOwnerQuery(event.target.value)}
                placeholder="Search by owner name"
                aria-label="Search by owner name"
              />
            )}
            {ownerQuery.trim() && (
              ownerSearchResults.length ? (
                <ul>
                  {ownerSearchResults.map((entry) => (
                    <li key={`${entry.displayName}-${entry.startPage}`}>
                      <button type="button" onClick={() => changePage(entry.startPage)}>
                        <strong>{entry.displayName}</strong>
                        <span>Pages {entry.startPage}–{entry.endPage}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p>No owner match found{ownerIndex.status === 'indexing' ? ' in the indexed pages yet' : ''}.</p>
            )}
          </section>
        )}

        <div className="home-document-viewer__preview">
        {error ? (
          <p className="home-document-viewer__status cao-document-viewer__status cao-document-viewer__status--error">{error}</p>
        ) : isLoadingFile || !pdfUrl ? (
          <p className="home-document-viewer__status cao-document-viewer__status">{loadingPreviewMessage}</p>
        ) : isPdf ? (
          <Document
            file={pdfUrl}
            loading={<p className="home-document-viewer__status cao-document-viewer__status">{loadingPreviewMessage}</p>}
            onLoadSuccess={(loadedPdf) => {
              setPdfDocument(loadedPdf);
              setPageCount(loadedPdf.numPages);
              setPageNumber(1);
              setDisplayedPage(1);
              setError('');
            }}
            onLoadError={(loadError) => {
              console.error('Failed to load Documents PDF preview:', loadError);
              setError('Unable to load PDF preview. Use Download or open the PDF in a new tab.');
            }}
          >
            {pageCount > 0 && (
              <div className="home-document-viewer__pdf-stage cao-document-viewer__pdf-stage cao-document-viewer__pdf-stage--page-view">
                {isPageRendering && (
                  <div className="home-document-viewer__pdf-loader cao-document-viewer__pdf-loader" role="status" aria-live="polite">
                    <div className="cao-document-viewer__page-placeholder" aria-hidden="true">
                      <span className="cao-document-viewer__page-placeholder-line" />
                      <span className="cao-document-viewer__page-placeholder-line cao-document-viewer__page-placeholder-line--short" />
                    </div>
                  </div>
                )}

                <PdfSideNavigation
                  currentPage={pageNumber}
                  pageCount={pageCount}
                  disabled={isPageRendering}
                  onPrevious={() => changePage(pageNumber - 1)}
                  onNext={() => changePage(pageNumber + 1)}
                >
                  <div className="home-document-viewer__pdf-page cao-document-viewer__page">
                    <Page
                      pageNumber={displayedPage}
                      width={viewerWidth || undefined}
                      scale={zoom / 100}
                      rotate={rotation}
                      renderMode={appearance === 'dark' ? 'custom' : 'canvas'}
                      customRenderer={DarkModePdfCanvas}
                      renderAnnotationLayer={false}
                      renderTextLayer={appearance === 'dark'}
                      onRenderSuccess={() => {
                        if (displayedPage === pageNumber) markRenderComplete('canvas', pageNumber);
                      }}
                      onRenderTextLayerSuccess={() => {
                        if (displayedPage === pageNumber) markRenderComplete('text', pageNumber);
                      }}
                      onRenderTextLayerError={() => {
                        if (displayedPage === pageNumber) markRenderComplete('text', pageNumber);
                      }}
                    />
                  </div>
                </PdfSideNavigation>

                {displayedPage !== pageNumber && (
                  <div className="home-document-viewer__pdf-page home-document-viewer__pdf-page--preloading cao-document-viewer__page cao-document-viewer__page--preloading" aria-hidden="true">
                    <Page
                      pageNumber={pageNumber}
                      width={viewerWidth || undefined}
                      scale={zoom / 100}
                      rotate={rotation}
                      renderMode={appearance === 'dark' ? 'custom' : 'canvas'}
                      customRenderer={DarkModePdfCanvas}
                      renderAnnotationLayer={false}
                      renderTextLayer={appearance === 'dark'}
                      onRenderSuccess={() => markRenderComplete('canvas', pageNumber)}
                      onRenderTextLayerSuccess={() => markRenderComplete('text', pageNumber)}
                      onRenderTextLayerError={() => markRenderComplete('text', pageNumber)}
                    />
                  </div>
                )}
              </div>
            )}
          </Document>
        ) : isImage ? (
          <div className="home-document-viewer__image-stage">
            <img
              src={pdfUrl}
              alt={file.FileName || 'Document preview'}
              style={{ transform: `scale(${zoom / 100}) rotate(${rotation}deg)` }}
            />
          </div>
        ) : isExcel ? (
          <div className="cao-document-viewer__excel">
            <div className="cao-document-viewer__excel-controls">
              <div className="cao-document-viewer__excel-toolbar">
                <div className="cao-document-viewer__excel-sheet-picker">
                  <span className="cao-document-viewer__excel-label">Sheet</span>
                  <select className="cao-document-viewer__excel-select" value={activeSheet} onChange={(event) => {
                const sheet = event.target.value;
                setExcelFilters({});
                setExcelSortConfig({ column: null, direction: 'asc' });
                setActiveSheet(sheet);
              }}>
                {excelSheets.map((sheet) => <option key={sheet.name} value={sheet.name}>{sheet.name}</option>)}
                  </select>
                </div>
                <div className="cao-document-viewer__excel-toolbar-right">
                  <span className="cao-document-viewer__excel-summary">
                    {filteredExcelRows.length}{Object.keys(excelFilters).length ? ` of ${excelBodyRows.length}` : ''} rows | {excelColumnCount} columns
                  </span>
                </div>
              </div>
            </div>
            <div className="cao-document-viewer__excel-wrap">
              <table className="cao-document-viewer__excel-table">
                <thead>
                  <tr>
                    <th className="cao-document-viewer__excel-corner" aria-hidden="true"></th>
                    {Array.from({ length: excelColumnCount }, (_, columnIndex) => (
                      <th key={columnIndex} className="cao-document-viewer__excel-column" aria-sort={excelSortConfig.column === columnIndex ? (excelSortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" className={`cao-document-viewer__excel-sort-button${excelSortConfig.column === columnIndex ? ' cao-document-viewer__excel-sort-button--active' : ''}`} onClick={() => handleExcelSort(columnIndex)}>
                          <span className="cao-document-viewer__excel-sort-letter">{getExcelColumnLabel(columnIndex)}</span>
                          <span className="cao-document-viewer__excel-sort-title">{String(excelHeaderRow[columnIndex] ?? '').trim() || `Column ${getExcelColumnLabel(columnIndex)}`}</span>
                          {excelSortConfig.column === columnIndex && <span className="cao-document-viewer__excel-sort-indicator" aria-hidden="true">{excelSortConfig.direction === 'asc' ? UP_ARROW : DOWN_ARROW}</span>}
                        </button>
                      </th>
                    ))}
                  </tr>
                  <tr className="cao-document-viewer__excel-filter-row">
                    <th className="cao-document-viewer__excel-filter-index">Search</th>
                    {Array.from({ length: excelColumnCount }, (_, columnIndex) => (
                      <th key={columnIndex} className="cao-document-viewer__excel-filter-cell">
                        <input type="text" className="cao-document-viewer__excel-filter-input" value={excelFilters[columnIndex] || ''} onChange={(event) => handleExcelFilterChange(columnIndex, event.target.value)} placeholder={String(excelHeaderRow[columnIndex] ?? '').trim() || getExcelColumnLabel(columnIndex)} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedExcelRows.map(({ row, sheetRowNumber }) => (
                    <tr key={sheetRowNumber}>
                      <th className="cao-document-viewer__excel-row">{sheetRowNumber}</th>
                      {Array.from({ length: excelColumnCount }, (_, columnIndex) => <td key={columnIndex} className="cao-document-viewer__excel-cell">{row[columnIndex] === '' || row[columnIndex] == null ? '\u00A0' : String(row[columnIndex])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (isEml || isMsg) && emailPreview ? (
          <div className="cao-document-viewer__email">
            <article className="cao-document-viewer__email-card">
              <header className="cao-document-viewer__email-header">
                <span className="cao-document-viewer__email-label">Subject</span>
                <h2 className="cao-document-viewer__email-subject">{emailPreview.subject}</h2>
              </header>
              <div className="cao-document-viewer__email-fields">
                {[
                  ['From', emailPreview.from],
                  ['To', emailPreview.to],
                  ['Cc', emailPreview.cc],
                  ['Bcc', emailPreview.bcc],
                  ['Date', emailPreview.date]
                ].filter(([, value]) => value).map(([label, value]) => (
                  <div className="cao-document-viewer__email-field" key={label}>
                    <span className="cao-document-viewer__email-label">{label}</span>
                    <span className="cao-document-viewer__email-value">{value}</span>
                  </div>
                ))}
              </div>
              {emailPreview.attachments?.length > 0 && (
                <section className="cao-document-viewer__email-attachments">
                  <span className="cao-document-viewer__email-label">Attachments</span>
                  <ul className="cao-document-viewer__email-attachment-list">
                    {emailPreview.attachments.map((attachment, index) => {
                      const isEmbeddedWord = /\.docx$/i.test(attachment.fileName);
                      return (
                        <li className="cao-document-viewer__email-attachment-item" key={`${attachment.fileName}-${index}`}>
                          <span className="cao-document-viewer__email-attachment-name">{attachment.fileName}</span>
                          <span className="cao-document-viewer__email-attachment-meta">
                            {attachment.mimeType} | {attachment.blob.size.toLocaleString()} bytes
                          </span>
                          <div className="cao-file-actions cao-document-viewer__email-attachment-actions">
                            <button
                              type="button"
                              className="view-button cao-file-actions__icon-button"
                              disabled={isEmbeddedWord}
                              onClick={() => setEmbeddedAttachment(attachment)}
                              title={isEmbeddedWord ? 'Embedded Word files must be downloaded to open in Word.' : 'View attachment'}
                              aria-label={isEmbeddedWord ? 'Embedded Word attachment preview unavailable' : `View ${attachment.fileName}`}
                            >
                              <span aria-hidden="true">↗</span>
                            </button>
                            <button
                              type="button"
                              className="view-button cao-file-actions__icon-button"
                              onClick={() => downloadEmbeddedAttachment(attachment)}
                              title="Download attachment"
                              aria-label={`Download ${attachment.fileName}`}
                            >
                              <span aria-hidden="true">↓</span>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
              <section className="cao-document-viewer__email-message">
                <span className="cao-document-viewer__email-label">
                  {emailThreadMessages.length > 1 ? `Conversation · ${emailThreadMessages.length} messages` : 'Message'}
                </span>
                <div className="cao-document-viewer__email-thread">
                  {(emailThreadMessages.length ? emailThreadMessages : ['(No message body)']).map((message, index) => (
                    <article className="cao-document-viewer__email-thread-message" key={`${index}-${message.slice(0, 40)}`}>
                      {emailThreadMessages.length > 1 && (
                        <span className="cao-document-viewer__email-thread-label">
                          {index === 0 ? 'Current message' : `Previous reply ${index}`}
                        </span>
                      )}
                      <pre className="cao-document-viewer__email-body-text">{message}</pre>
                    </article>
                  ))}
                </div>
              </section>
            </article>
          </div>
        ) : (
          <iframe className="home-document-viewer__frame" src={pdfUrl} title={file.FileName || 'Document preview'} />
        )}
        </div>
      </main>
    </div>
  );
};

export default HomeDocumentViewer;
